import moment from 'moment-timezone';
import React, { useEffect, useRef, FC, useContext, Dispatch, SetStateAction } from 'react';
import styled, { useTheme } from 'styled-components';

import { SOCKET_SUB_TYPE, TRADING_TYPE, TRADING_VIEW_URL } from 'src/common/constants';
import { getResolution, isEmptyObj, parseTradeSettings } from 'src/common/helpers';
import { Spinner } from 'src/components/core';
import { FlexContainer, StyledFlexContainer } from 'src/components/core/layout/FlexContainer';
import ChartHeader from 'src/components/features/common/components/ChartHeader';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import {
    widget as TradingViewWidget,
    ChartingLibraryWidgetOptions,
    ResolutionString,
    ThemeName,
    EntityInfo,
    CustomTimezones,
    Timezone,
} from 'src/lib/charting_library';
import { updateChartInterval } from 'src/setup/redux/slices/globalSlice';
import { updateTvWidgetRef } from 'src/setup/redux/slices/spotSlice';
import store from 'src/setup/redux/Store';
import SocketContext from 'src/setup/socket/socketContext';
import { Symbol } from 'src/types';

import DataFeed from './dataFeed';
import DepthChart from './depth';

type ChartColors = {
    up: string;
    down: string;
    bg: string;
    grid: string;
    grid2: string;
    cross: string;
    border: string;
    text: string;
    areaTop: string;
    areaDown: string;
};

interface ChartProps {
    activeTab?: number;
    activeChart?: string;
    setActiveChart?: Dispatch<SetStateAction<string>>;
}

const StyledChart = styled(StyledFlexContainer)<{ activeTab?: number }>`
    flex-direction: column;
    width: 100%;
    height: 100%;
`;

const ChartWrapper = styled.div`
    position: relative;
    width: inherit;
    height: inherit;
`;

const Chart: FC<ChartProps> = ({ activeTab, activeChart, setActiveChart }) => {
    const { colors, isMobile } = useTheme();
    const { currentSymbol, tvWidgetRef } = useAppSelector((state) => state.spot);
    const { themeMode, spotSettings: chartSettings } = useAppSelector((state) => state.global);
    const isSymbolChange = useRef(false);
    const initializeChartCompleted = useRef(false);
    const widgetRef = useRef<any>(null);
    const spotSettings = parseTradeSettings(chartSettings);
    const chartIntervalRef = useRef(spotSettings.chartInterval);
    const symbolRef = useRef({} as Symbol);
    const dispatch = useAppDispatch();
    const { subscribeTopic } = useContext(SocketContext);

    useEffect(() => {
        (async () => {
            if (document.getElementById('tv_chart_container') && !isEmptyObj(currentSymbol)) {
                symbolRef.current = currentSymbol;
                if (!tvWidgetRef) {
                    initializeChartCompleted.current = false;
                    initializeChart(currentSymbol);
                } else {
                    try {
                        const isChartReady = tvWidgetRef.current._ready;
                        if (isChartReady) {
                            isSymbolChange.current = true;
                            const chart = tvWidgetRef?.current.chart();
                            await chart.setSymbol(currentSymbol.symbolName, () => {
                                isSymbolChange.current = false;
                            });
                            chart.executeActionById('chartReset');
                            chart.executeActionById('timeScaleReset');
                        }
                    } catch (error) {
                        initializeChartCompleted.current = false;
                        initializeChart(currentSymbol);
                    }
                }
            }
        })();
    }, [currentSymbol]);

    useEffect(() => {
        try {
            if (tvWidgetRef && tvWidgetRef.current._ready && initializeChartCompleted.current) {
                tvWidgetRef.current.changeTheme(themeMode).then(() => {
                    const chartTheme: ChartColors = getChatColors();
                    tvWidgetRef.current.applyOverrides(
                        charOverrides({
                            ...chartTheme,
                            grid2: themeMode === 'light' ? '#B0BFDB' : '#475563',
                        }),
                    );
                });
            }
            // eslint-disable-next-line no-empty
        } catch (error) {}
    }, [themeMode]);

    useEffect(() => {
        if (activeTab === 0) {
            setActiveChart && activeChart && setActiveChart('chart');
        } else if (activeTab === 1) {
            setActiveChart && activeChart && setActiveChart('depth');
        }
    }, [activeTab]);

    const initializeChart = (symbol: Symbol) => {
        const overrides = charOverrides(getChatColors());
        const guessedTimezone = moment.tz.guess();
        const timezone = moment.tz.zone(guessedTimezone);
        let selectedTimezone: CustomTimezones | Timezone = 'Etc/UTC';
        if (timezone) {
            selectedTimezone = timezone.name as CustomTimezones;
        }
        const widgetOptions: ChartingLibraryWidgetOptions = {
            // debug: true,
            symbol: symbol.symbolName,
            theme: themeMode as ThemeName,
            interval: spotSettings.chartInterval as ResolutionString,
            fullscreen: false,
            datafeed: new DataFeed(store),
            library_path: '/trade/static/charting_library/',
            timezone: selectedTimezone,
            loading_screen: { backgroundColor: 'transparent' },
            auto_save_delay: 3,
            saved_data: window.localStorage['tradingview.analysis.spotData']
                ? JSON.parse(window.localStorage['tradingview.analysis.spotData'])
                : undefined,
            drawings_access: {
                type: 'black',
                tools: [
                    {
                        name: 'Regression Trend',
                    },
                ],
            },
            disabled_features: [
                // 'compare_symbol',
                'display_market_status',
                // 'go_to_date',
                'header_compare',
                'header_symbol_search',
                'header_undo_redo',
                'symbol_search_hot_key',
                'header_widget',
                // 'header_chart_type',
                // 'header_interval_dialog_button',
                // 'header_resolutions',
                // 'header_screenshot',
                // 'header_saveload',
                // 'legend_context_menu',
                // 'show_hide_button_in_legend',
                // 'show_interval_dialog_on_key_press',
                // 'snapshot_trading_drawings',
                'symbol_info',
                // 'use_localstorage_for_settings',
                // 'volume_force_overlay',
                // 'timeframes_toolbar',
            ],
            enabled_features: [
                'dont_show_boolean_study_arguments',
                'hide_last_na_study_output',
                // 'move_logo_to_main_pane',
                // 'same_data_requery',
                'side_toolbar_in_fullscreen_mode',
                'disable_resolution_rebuild',
                // 'layout_about_to_be_changed',
                // 'keep_left_toolbar_visible_on_small_screens',
            ],
            // charts_storage_url: 'https://saveload.tradingview.com',
            charts_storage_api_version: '1.1',
            favorites: {
                intervals: ['5', '15', '60', '240', 'D', '1W'] as ResolutionString[],
                chartTypes: ['Line'],
            },
            overrides,
            custom_css_url: '24.05.23.css',
            toolbar_bg: 'transparent',
            autosize: true,
            studies_overrides: {
                'volume.volume.color.0': 'rgba(246, 92, 92, 0.3)',
                'volume.volume.color.1': 'rgba(62, 183, 114, 0.3)',
            },
            time_frames: [],
            ...(isMobile && { preset: 'mobile' }),
            container: 'tv_chart_container',
            locale: 'en',
        };

        const widget = new (TradingViewWidget as any)(widgetOptions);
        widget.onChartReady(() => {
            const chartTheme: ChartColors = getChatColors();
            widget.changeTheme(themeMode).then(() => {
                widget.applyOverrides(
                    charOverrides({
                        ...chartTheme,
                        grid2: themeMode === 'light' ? '#B0BFDB' : '#475563',
                    }),
                );
            });

            initChartAnalysis(widget);
            widget
                .chart()
                .onIntervalChanged()
                .subscribe(null, function (interval: string) {
                    if (!isSymbolChange.current) {
                        widget.chart().executeActionById('chartReset');
                        widget.chart().executeActionById('timeScaleReset');
                        dispatch(
                            updateChartInterval({
                                type: 'spotSettings',
                                interval: interval,
                            }),
                        );
                        chartIntervalRef.current = interval;
                        const resolution = getResolution(interval);
                        subscribeTopic(SOCKET_SUB_TYPE.TRADE_DATA, {
                            ['kline']: {
                                id: `kline_${symbolRef.current.exchangeId}${symbolRef.current.symbolId}${resolution}`,
                                topic: `kline_${resolution}`,
                                event: 'sub',
                                symbol: `${symbolRef.current.exchangeId}.${symbolRef.current.symbolId}`,
                                params: {
                                    binary: true,
                                    klineType: resolution,
                                    limit: 1500,
                                    realtimeInterval: '24h',
                                },
                            },
                        });
                        saveChartAnalysis(widget);
                    }
                });
            widget.subscribe('onAutoSaveNeeded', () => {
                saveChartAnalysis(widget);
            });
            widget.subscribe('onScreenshotReady', (url: string) => {
                if (url !== '') {
                    window.open(`${TRADING_VIEW_URL}${url}`, '_blank');
                }
            });
            setTimeout(() => {
                widget.chart().executeActionById('timeScaleReset');
            }, 400);
        });
        widgetRef.current = widget;
        dispatch(updateTvWidgetRef(widgetRef));
        initializeChartCompleted.current = true;
        symbolRef.current = currentSymbol;
    };

    const initChartAnalysis = async (widget: any) => {
        if (!widget) {
            return;
        }
        const chart = widget.chart();
        const currentStudies: EntityInfo[] = chart.getAllStudies();
        let activeStudyNames: string[] = [] as string[];
        currentStudies.map(async (study: EntityInfo) => {
            activeStudyNames.push(study.name);
        });
        if (!activeStudyNames.includes('Volume')) {
            await chart.createStudy('Volume');
        }
        chart.executeActionById('timeScaleReset');
    };

    const saveChartAnalysis = (widget: any) => {
        widget.save((res: any) => {
            window.localStorage.setItem('tradingview.analysis.spotData', JSON.stringify(res));
        });
    };

    const charOverrides = (chartColors: ChartColors) => {
        return {
            'scalesProperties.showStudyLastValue': false,
            'scalesProperties.showSymbolLabels': false,
            volumePaneSize: 'medium',
            'scalesProperties.lineColor': chartColors.grid2,
            'scalesProperties.textColor': chartColors.text,
            'paneProperties.background': chartColors.bg,
            'paneProperties.backgroundType': 'solid',
            'paneProperties.vertGridProperties.color': chartColors.grid,
            'paneProperties.horzGridProperties.color': chartColors.grid,
            'paneProperties.crossHairProperties.color': chartColors.cross,
            // 'paneProperties.crossHairProperties.style': '',
            // 'paneProperties.crossHairProperties.width': 0,
            'paneProperties.legendProperties.showLegend': true,
            'paneProperties.legendProperties.showStudyArguments': true,
            'paneProperties.legendProperties.showStudyTitles': true,
            'paneProperties.legendProperties.showStudyValues': true,
            'paneProperties.legendProperties.showSeriesTitle': true,
            'paneProperties.legendProperties.showSeriesOHLC': true,
            // Series style. See the supported values below
            // Bars = 0
            // Candles = 1
            // Line = 2
            // Area = 3
            // Heikin Ashi = 8
            // Hollow Candles = 9
            // Renko = 4
            // Kagi = 5
            // Point&Figure = 6
            // Line Break = 7
            // Baseline = 10
            'mainSeriesProperties.style': spotSettings.activeChartType,
            'mainSeriesProperties.candleStyle.upColor': chartColors.up,
            'mainSeriesProperties.candleStyle.downColor': chartColors.down,
            'mainSeriesProperties.candleStyle.drawWick': true,
            'mainSeriesProperties.candleStyle.drawBorder': true,
            'mainSeriesProperties.candleStyle.borderColor': chartColors.border,
            'mainSeriesProperties.candleStyle.borderUpColor': chartColors.up,
            'mainSeriesProperties.candleStyle.borderDownColor': chartColors.down,
            'mainSeriesProperties.candleStyle.wickUpColor': chartColors.up,
            'mainSeriesProperties.candleStyle.wickDownColor': chartColors.down,
            'mainSeriesProperties.candleStyle.barColorsOnPrevClose': false,
            'mainSeriesProperties.hollowCandleStyle.upColor': chartColors.up,
            'mainSeriesProperties.hollowCandleStyle.downColor': chartColors.down,
            'mainSeriesProperties.hollowCandleStyle.drawWick': true,
            'mainSeriesProperties.hollowCandleStyle.drawBorder': true,
            'mainSeriesProperties.hollowCandleStyle.borderColor': chartColors.border,
            'mainSeriesProperties.hollowCandleStyle.borderUpColor': chartColors.up,
            'mainSeriesProperties.hollowCandleStyle.borderDownColor': chartColors.down,
            // 'mainSeriesProperties.hollowCandleStyle.wickColor': chartColors.line,
            'mainSeriesProperties.haStyle.upColor': chartColors.up,
            'mainSeriesProperties.haStyle.downColor': chartColors.down,
            'mainSeriesProperties.haStyle.drawWick': true,
            'mainSeriesProperties.haStyle.drawBorder': true,
            'mainSeriesProperties.haStyle.borderColor': chartColors.border,
            'mainSeriesProperties.haStyle.borderUpColor': chartColors.up,
            'mainSeriesProperties.haStyle.borderDownColor': chartColors.down,
            'mainSeriesProperties.haStyle.wickColor': chartColors.border,
            'mainSeriesProperties.haStyle.barColorsOnPrevClose': false,
            'mainSeriesProperties.barStyle.upColor': chartColors.up,
            'mainSeriesProperties.barStyle.downColor': chartColors.down,
            'mainSeriesProperties.barStyle.barColorsOnPrevClose': false,
            'mainSeriesProperties.barStyle.dontDrawOpen': false,
            'mainSeriesProperties.lineStyle.color': chartColors.border,
            'mainSeriesProperties.lineStyle.linewidth': 1,
            'mainSeriesProperties.lineStyle.priceSource': 'close',
            'mainSeriesProperties.areaStyle.color1': chartColors.areaTop,
            'mainSeriesProperties.areaStyle.color2': chartColors.areaDown,
            'mainSeriesProperties.areaStyle.linecolor': chartColors.border,
            'mainSeriesProperties.areaStyle.linewidth': 1,
            'mainSeriesProperties.areaStyle.priceSource': 'close',
        };
    };

    const getChatColors = () => {
        return {
            up: colors.exchange.buy,
            down: colors.exchange.sell,
            bg: colors.background.primary,
            grid: 'rgba(194,194,194,0.08)',
            grid2: themeMode === 'light' ? '#B0BFDB' : '#475563',
            cross: 'rgba(104,119,139,.7)',
            border: colors.border.dark,
            text: colors.text.tertiary,
            areaTop: 'rgba(153,248,167,0.5)',
            areaDown: 'rgba(153,248,167,0.01)',
        };
    };

    return (
        <StyledChart activeTab={activeTab} id="chartMainContainer">
            <ChartWrapper>
                <ChartHeader
                    tvWidgetRef={tvWidgetRef}
                    activeTab={activeTab}
                    activeChart={activeChart}
                    favoriteChartTypes={spotSettings.favoriteChartTypes}
                    activeChartType={spotSettings.activeChartType}
                    favoriteIntervals={spotSettings.favoriteIntervals}
                    chartInterval={spotSettings.chartInterval}
                    tradeType={TRADING_TYPE.SPOT}
                />
                <div
                    id="tv_chart_container"
                    ref={(ref) => ref}
                    style={
                        (activeTab && activeTab != 0) || (activeChart && activeChart !== 'chart')
                            ? { display: 'none' }
                            : !tvWidgetRef?.current._ready
                            ? { visibility: 'hidden' }
                            : {}
                    }
                />
                {!tvWidgetRef?.current._ready && activeChart == 'chart' && (
                    <FlexContainer
                        styles={{
                            width: '100%',
                            height: '100%',
                            justifyContent: 'center',
                            position: 'absolute',
                            top: '0',
                            margin: '0 auto',
                        }}
                    >
                        <Spinner size="24" />
                    </FlexContainer>
                )}
                <DepthChart activeChart={activeChart} />
            </ChartWrapper>
        </StyledChart>
    );
};

export default Chart;
